// src/components/news/NewsContent.jsx
import React, { useState, useMemo, useEffect } from "react";
import NewsCard from "./NewsCard";
import {
  CalendarIcon,
  MagnifyingGlassIcon,
  TagIcon,
} from "@heroicons/react/24/outline";
import { pickUrlStringFromNewsItem } from "../../utils/newsLink";

/**
 * Collect plain text from Strapi-style rich text blocks for client-side search.
 */
function collectRichTextStrings(node, out) {
  if (node == null) return;
  if (typeof node === "string") {
    out.push(node);
    return;
  }
  if (typeof node === "object") {
    if (typeof node.text === "string") {
      out.push(node.text);
    }
    if (Array.isArray(node.children)) {
      node.children.forEach((child) => collectRichTextStrings(child, out));
    }
  }
}

function richTextBlocksToSearchBlob(blocks) {
  if (!blocks || !Array.isArray(blocks)) return "";
  const parts = [];
  blocks.forEach((block) => collectRichTextStrings(block, parts));
  return parts.join(" ");
}

const NewsContent = ({ data }) => {
  if (!data) return null;

  const banner = data.banner;
  const filterConfig = data.filter;
  // cas_news can be either an object with .list or an array with [0].list
  const rawNewsList =
    data.cas_news?.list || data.cas_news?.[0]?.list || [];

  // Filter states
  const [selectedYear, setSelectedYear] = useState("All");
  const [selectedProduct, setSelectedProduct] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchKey, setDebouncedSearchKey] = useState("");

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setDebouncedSearchKey(searchQuery.trim().toLowerCase());
    }, 280);
    return () => window.clearTimeout(handle);
  }, [searchQuery]);

  // Normalize news items into structure expected by NewsCard & filters
  const newsItems = useMemo(() => {
    if (!rawNewsList || rawNewsList.length === 0) return [];

    return rawNewsList.map((item) => {
      const id = item.id || item.documentId;
      const date = item.newsDate || null;
      const title = item.newsTitle || "";
      const product = item.newsProduct || "";
      const subtitle = item.newsSubtitle || "";
      const content = item.newsContent || null;
      const media = item.media || [];
      const youtube_id = item.youtube_id || "";
      const url = pickUrlStringFromNewsItem(item);

      return {
        ...item,
        id,
        date,
        title,
        product,
        subtitle,
        content,
        media,
        youtube_id,
        url,
      };
    });
  }, [rawNewsList]);

  // Get unique years and products from news data
  const availableYears = useMemo(() => {
    if (!newsItems || newsItems.length === 0) {
      return ["All"];
    }

    const years = newsItems
      .map((item) => {
        if (!item.date) return null;
        try {
          const dateObj = new Date(item.date);
          if (!isNaN(dateObj.getTime())) {
            return dateObj.getFullYear();
          }
        } catch (e) {
          return null;
        }
        return null;
      })
      .filter((year) => year !== null)
      .filter((year, index, self) => self.indexOf(year) === index)
      .sort((a, b) => b - a);

    return ["All", ...years.map((year) => year.toString())];
  }, [newsItems]);

  const availableProducts = useMemo(() => {
    if (!newsItems || newsItems.length === 0) {
      return ["All"];
    }

    const products = newsItems
      .map((item) => {
        const product = item.product || item.newsProduct || "";
        return product.trim();
      })
      .filter((product) => product.length > 0)
      .filter((product, index, self) => self.indexOf(product) === index)
      .sort();

    return ["All", ...products];
  }, [newsItems]);

  // Filter news based on selected filters and optional search text
  const filteredNews = useMemo(() => {
    if (!newsItems || newsItems.length === 0) return [];

    const searchTokens = searchQuery
      .trim()
      .toLowerCase()
      .split(/\s+/)
      .filter(Boolean);

    return newsItems
      .filter((item) => {
        let itemYear = null;
        if (item.date) {
          try {
            const dateObj = new Date(item.date);
            if (!isNaN(dateObj.getTime())) {
              itemYear = dateObj.getFullYear().toString();
            }
          } catch (e) {
            // ignore invalid date
          }
        }

        const yearMatch =
          selectedYear === "All" ||
          (itemYear !== null && itemYear === selectedYear);

        const itemProduct = (item.product || item.newsProduct || "").trim();
        const normalizedSelectedProduct = selectedProduct.trim();

        const productMatch =
          selectedProduct === "All" ||
          (itemProduct.length > 0 &&
            itemProduct === normalizedSelectedProduct);

        if (!yearMatch || !productMatch) {
          return false;
        }

        if (searchTokens.length === 0) {
          return true;
        }

        const contentBlob = richTextBlocksToSearchBlob(item.content);
        const haystack = [
          item.title,
          item.subtitle,
          item.product,
          item.newsProduct,
          contentBlob,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();

        return searchTokens.every((token) => haystack.includes(token));
      })
      .sort((a, b) => {
        try {
          const dateA = a.date ? new Date(a.date) : new Date(0);
          const dateB = b.date ? new Date(b.date) : new Date(0);
          if (isNaN(dateA.getTime())) return 1;
          if (isNaN(dateB.getTime())) return -1;
          return dateB - dateA;
        } catch (e) {
          return 0;
        }
      });
  }, [newsItems, selectedYear, selectedProduct, searchQuery]);

  const filterTitle = filterConfig?.title || "Filter News";
  const filterYearLabel = filterConfig?.filterYear || "Filter by Year";
  const filterProductLabel =
    filterConfig?.filterProducts || "Filter by Product";
  const searchLabel = filterConfig?.searchLabel || "Search";
  const searchPlaceholder =
    filterConfig?.searchPlaceholder || "Search news...";

  const newsListAnimationKey = `${selectedYear}|${selectedProduct}|${debouncedSearchKey}`;

  return (
    <section className="section-surface-lake w-full py-12 md:py-16 lg:py-20">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 flex flex-col lg:flex-row gap-8 lg:gap-12">
        {/* Left: Filter navigation */}
        <aside className="lg:w-64 xl:w-72 lg:sticky lg:top-24 lg:self-start">
          <div className="panel-elevated rounded-2xl p-4 md:p-5 lg:p-6">
            <h2 className="text-lg md:text-xl font-bold text-gray-900 mb-4 md:mb-5">
              {filterTitle}
            </h2>
            <div className="space-y-5">
              {/* Search */}
              <div>
                <label
                  htmlFor="news-search"
                  className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-3"
                >
                  <MagnifyingGlassIcon className="h-5 w-5 text-lake-500" />
                  {searchLabel}
                </label>
                <input
                  id="news-search"
                  type="search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={searchPlaceholder}
                  autoComplete="off"
                  className="w-full px-4 py-3 bg-white border-2 border-gray-300 rounded-lg text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-lake-400 focus:border-transparent transition-all duration-200 hover:border-mist-400"
                />
              </div>

              {/* Year Filter */}
              <div>
                <label
                  htmlFor="year-filter"
                  className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-3"
                >
                  <CalendarIcon className="h-5 w-5 text-lake-500" />
                  {filterYearLabel}
                </label>
                <select
                  id="year-filter"
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value)}
                  className="w-full px-4 py-3 bg-white border-2 border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-lake-400 focus:border-transparent transition-all duration-200 cursor-pointer hover:border-mist-400"
                >
                  {availableYears.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>

              {/* Product Filter */}
              <div>
                <label
                  htmlFor="product-filter"
                  className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-3"
                >
                  <TagIcon className="h-5 w-5 text-lake-500" />
                  {filterProductLabel}
                </label>
                <select
                  id="product-filter"
                  value={selectedProduct}
                  onChange={(e) => setSelectedProduct(e.target.value)}
                  className="w-full px-4 py-3 bg-white border-2 border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-lake-400 focus:border-transparent transition-all duration-200 cursor-pointer hover:border-mist-400"
                >
                  {availableProducts.map((product) => (
                    <option key={product} value={product}>
                      {product}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </aside>

        {/* Right: News List Section */}
        <div className="flex-1">
          <div className="space-y-6" key={newsListAnimationKey}>
            {filteredNews.length === 0 ? (
              <div className="panel-elevated rounded-2xl p-8 md:p-12 text-center support-tab-fade-in">
                <p className="text-gray-600 text-lg">
                  No news found matching your filters or search.
                </p>
              </div>
            ) : (
              filteredNews.map((item, index) => (
                <NewsCard
                  key={item.id || index}
                  newsItem={item}
                  index={index}
                />
              ))
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default NewsContent;



